import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function FFMOperateurs() {
  const [operateurs, setOperateurs] = useState([]);
  const [profils, setProfils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOperateur, setEditingOperateur] = useState(null);
  const [recherche, setRecherche] = useState(false);
  const [rechercheTexte, setRechercheTexte] = useState('');
  const [formData, setFormData] = useState({ prenom: '', nom: '', email: '', telephone: '', profil_id: '', actif: true });
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const [{ data: ops }, { data: profs }] = await Promise.all([
      supabase.from('ffm_operateurs').select('*, ffm_profils(nom, onglets_accessibles)').order('nom', { ascending: true }),
      supabase.from('ffm_profils').select('id, nom').order('nom', { ascending: true })
    ]);
    setOperateurs(ops || []);
    setProfils(profs || []);
    setLoading(false);
  };

  const openModal = (op = null) => {
    setErreur('');
    if (op) {
      setEditingOperateur(op);
      setFormData({
        prenom: op.prenom,
        nom: op.nom,
        email: op.email,
        telephone: op.telephone || '',
        profil_id: op.profil_id || '',
        actif: op.actif
      });
    } else {
      setEditingOperateur(null);
      setFormData({ prenom: '', nom: '', email: '', telephone: '', profil_id: '', actif: true });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOperateur(null);
    setFormData({ prenom: '', nom: '', email: '', telephone: '', profil_id: '', actif: true });
    setErreur('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!formData.prenom.trim() || !formData.nom.trim()) {
      setErreur('Le prénom et le nom sont obligatoires.');
      return;
    }
    if (!formData.email.trim()) {
      setErreur('L\'email est obligatoire.');
      return;
    }
    if (!formData.profil_id) {
      setErreur('Veuillez sélectionner un profil.');
      return;
    }

    const payload = {
      prenom: formData.prenom.trim(),
      nom: formData.nom.trim(),
      email: formData.email.trim().toLowerCase(),
      telephone: formData.telephone.trim() || null,
      profil_id: formData.profil_id,
      actif: formData.actif
    };

    if (editingOperateur) {
      const { error } = await supabase.from('ffm_operateurs').update(payload).eq('id', editingOperateur.id);
      if (error) {
        setErreur(error.code === '23505' ? 'Cet email est déjà utilisé.' : 'Erreur lors de la modification.');
        return;
      }
    } else {
      const { error } = await supabase.from('ffm_operateurs').insert([payload]);
      if (error) {
        setErreur(error.code === '23505' ? 'Cet email est déjà utilisé.' : 'Erreur lors de la création.');
        return;
      }
    }

    loadAll();
    closeModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet opérateur ?')) return;
    const { error } = await supabase.from('ffm_operateurs').delete().eq('id', id);
    if (error) { console.error('Erreur suppression:', error); }
    else { loadAll(); }
  };

  const toggleActif = async (op) => {
    const { error } = await supabase.from('ffm_operateurs').update({ actif: !op.actif }).eq('id', op.id);
    if (!error) loadAll();
  };

  const initiales = (op) => `${op.prenom.charAt(0)}${op.nom.charAt(0)}`.toUpperCase();

  const operateursFiltres = operateurs.filter(op => {
    const q = rechercheTexte.toLowerCase();
    return (
      op.nom.toLowerCase().includes(q) ||
      op.prenom.toLowerCase().includes(q) ||
      op.email.toLowerCase().includes(q) ||
      (op.ffm_profils?.nom || '').toLowerCase().includes(q)
    );
  });

  // ─── Styles ──────────────────────────────────────────────────────────────────
  const styleBtnPrimary = {
    padding: '10px 20px', backgroundColor: '#003189', color: '#ffffff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
  };
  const styleBtnSecondary = {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#6c757d',
    border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
  };
  const styleBtnDanger = {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#dc3545',
    border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
  };
  const styleInput = {
    width: '100%', padding: '10px 14px', border: '1px solid #dee2e6',
    borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  };
  const styleLabel = {
    display: 'block', fontSize: '13px', fontWeight: '600', color: '#495057', marginBottom: '6px'
  };
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#212529' }}>Opérateurs FFM</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
            {operateurs.length} opérateur{operateurs.length > 1 ? 's' : ''} — accèdent au dashboard FFM via code email (OTP)
          </p>
        </div>
        <button
          style={profils.length === 0 ? { ...styleBtnPrimary, opacity: 0.5, cursor: 'not-allowed' } : styleBtnPrimary}
          onClick={() => profils.length > 0 && openModal()}
          title={profils.length === 0 ? 'Créez d\'abord un profil dans l\'onglet Profils' : ''}
        >
          + Nouvel opérateur
        </button>
      </div>

      {/* Avertissement si pas de profil */}
      {profils.length === 0 && (
        <div style={{
          padding: '14px 18px', backgroundColor: '#fff3cd', borderRadius: '10px',
          fontSize: '13px', color: '#856404', marginBottom: '20px',
          borderLeft: '4px solid #ffc107'
        }}>
          ⚠️ Aucun profil créé. Rendez-vous dans l'onglet <strong>Profils</strong> pour créer un profil avant d'ajouter des opérateurs.
        </div>
      )}

      {/* Recherche */}
      {operateurs.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <input
            style={{ ...styleInput, maxWidth: '360px' }}
            type="text"
            placeholder="Rechercher un opérateur..."
            value={rechercheTexte}
            onChange={e => setRechercheTexte(e.target.value)}
          />
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>Chargement...</div>
      ) : operateursFiltres.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>
            {operateurs.length === 0 ? 'Aucun opérateur créé' : 'Aucun résultat'}
          </div>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>
            {operateurs.length === 0 ? 'Cliquez sur "+ Nouvel opérateur" pour commencer.' : 'Essayez une autre recherche.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {operateursFiltres.map(op => (
            <div key={op.id} style={{
              backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e9ecef',
              padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px',
              opacity: op.actif ? 1 : 0.55
            }}>
              {/* Avatar */}
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%',
                backgroundColor: op.actif ? '#e8f0fe' : '#f1f3f5',
                color: op.actif ? '#003189' : '#adb5bd',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: '700', fontSize: '15px', flexShrink: 0
              }}>
                {initiales(op)}
              </div>

              {/* Infos */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#212529' }}>
                    {op.prenom} {op.nom}
                  </span>
                  <span style={{
                    fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px',
                    backgroundColor: op.actif ? '#d4edda' : '#f8d7da',
                    color: op.actif ? '#155724' : '#721c24'
                  }}>
                    {op.actif ? 'Actif' : 'Inactif'}
                  </span>
                  {op.ffm_profils && (
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px',
                      backgroundColor: '#e8f0fe', color: '#003189'
                    }}>
                      {op.ffm_profils.nom}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#6c757d' }}>✉️ {op.email}</span>
                  {op.telephone && (
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>📞 {op.telephone}</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styleBtnSecondary} onClick={() => toggleActif(op)}>
                  {op.actif ? 'Désactiver' : 'Activer'}
                </button>
                <button style={styleBtnSecondary} onClick={() => openModal(op)}>Modifier</button>
                <button style={styleBtnDanger} onClick={() => handleDelete(op.id)}>Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL ──────────────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 24px', fontSize: '18px', fontWeight: '700', color: '#212529' }}>
              {editingOperateur ? 'Modifier l\'opérateur' : 'Nouvel opérateur'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Prénom + Nom */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={styleLabel}>Prénom *</label>
                  <input style={styleInput} type="text" placeholder="Prénom"
                    value={formData.prenom}
                    onChange={e => setFormData(prev => ({ ...prev, prenom: e.target.value }))}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={styleLabel}>Nom *</label>
                  <input style={styleInput} type="text" placeholder="Nom"
                    value={formData.nom}
                    onChange={e => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styleLabel}>Email *</label>
                <input style={styleInput} type="email" placeholder="email@ffm.fr"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
                <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#adb5bd' }}>
                  L'opérateur se connectera au dashboard FFM via cet email (code OTP).
                </p>
              </div>

              {/* Téléphone */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styleLabel}>Téléphone <span style={{ color: '#adb5bd', fontWeight: '400' }}>(optionnel)</span></label>
                <input style={styleInput} type="tel" placeholder="06 00 00 00 00"
                  value={formData.telephone}
                  onChange={e => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                />
              </div>

              {/* Profil */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styleLabel}>Profil *</label>
                <select
                  style={{ ...styleInput, backgroundColor: '#ffffff' }}
                  value={formData.profil_id}
                  onChange={e => setFormData(prev => ({ ...prev, profil_id: e.target.value }))}
                >
                  <option value="">— Sélectionner un profil —</option>
                  {profils.map(p => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>

              {/* Actif */}
              {editingOperateur && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                    padding: '10px 14px', borderRadius: '8px', border: '1px solid #dee2e6'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.actif}
                      onChange={e => setFormData(prev => ({ ...prev, actif: e.target.checked }))}
                      style={{ width: '16px', height: '16px', accentColor: '#003189', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                      Compte actif (accès autorisé au dashboard FFM)
                    </span>
                  </label>
                </div>
              )}

              {erreur && (
                <div style={{
                  padding: '10px 14px', backgroundColor: '#f8d7da',
                  color: '#721c24', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                }}>
                  {erreur}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styleBtnSecondary} onClick={closeModal}>Annuler</button>
                <button type="submit" style={styleBtnPrimary}>
                  {editingOperateur ? 'Enregistrer' : 'Créer l\'opérateur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FFMOperateurs;
